import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Mail, ArrowLeft, Loader2, UserPlus, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import CalvioLogo from '@/components/CalvioLogo';
import LanguageSelector from '@/components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Handle Google credential (JWT) returned by @react-oauth/google's GoogleLogin button.
  // We exchange it for our own session via /api/auth/google.
  const handleGoogleCredential = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      toast.error(t('auth.google_failed', 'Google sign-in failed'));
      return;
    }
    setGoogleLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/google`,
        { credential },
        { withCredentials: true }
      );

      const sessionToken = response.data.session_token;
      if (sessionToken) {
        localStorage.setItem('calvio_session_token', sessionToken);
        localStorage.setItem('calvio_user', JSON.stringify(response.data));
      }
      await login(response.data, sessionToken);

      toast.success(
        response.data.is_new_user
          ? t('auth.welcome_new_user')
          : t('auth.welcome_back_user')
      );

      const redirectTo = response.data.onboarding_complete ? '/dashboard' : '/onboarding';
      setTimeout(() => {
        window.location.replace(redirectTo);
      }, 1200);
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error(error.response?.data?.detail || t('auth.google_failed', 'Google sign-in failed'));
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error(t('auth.google_failed', 'Google sign-in failed'));
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(t('auth.enter_email'));
      return;
    }

    if (mode === 'signup' && !name.trim()) {
      toast.error(t('auth.enter_name'));
      return;
    }
    
    setLoading(true);
    try {
      // For sign up, we send the name; for sign in, we don't need it
      // Always include the current language for email translation
      const payload = mode === 'signup' 
        ? { email, name: name.trim(), language: i18n.language }
        : { email, language: i18n.language };
      
      await axios.post(`${API}/auth/send-otp`, payload);
      toast.success(t('auth.code_sent'));
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.send_code_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (value) => {
    if (value.length !== 6) return;
    
    // Trim and normalize the OTP value
    const normalizedOTP = value.trim().toUpperCase();
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        email: email.trim().toLowerCase(),
        code: normalizedOTP,
        name: name.trim() || undefined
      }, { withCredentials: true });
      
      // Store token in localStorage FIRST before any navigation
      const sessionToken = response.data.session_token;
      if (sessionToken) {
        localStorage.setItem('calvio_session_token', sessionToken);
        localStorage.setItem('calvio_user', JSON.stringify(response.data));
        
        // Force a synchronous localStorage flush by reading back
        const verifyToken = localStorage.getItem('calvio_session_token');
        const verifyUser = localStorage.getItem('calvio_user');
        console.log('Login stored - Token exists:', !!verifyToken, 'User exists:', !!verifyUser);
      }
      
      // Update auth context
      await login(response.data, sessionToken);
      
      toast.success(mode === 'signup' ? t('auth.welcome_new_user') : t('auth.welcome_back_user'));
      
      // Determine redirect destination
      const redirectTo = response.data.onboarding_complete ? '/dashboard' : '/onboarding';
      
      // Use longer delay to ensure localStorage writes are complete before redirect
      setTimeout(() => {
        window.location.replace(redirectTo);
      }, 1500);
    } catch (error) {
      console.error('OTP verification error:', error);
      toast.error(error.response?.data?.detail || t('auth.invalid_code'));
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const payload = name.trim() 
        ? { email, name: name.trim(), language: i18n.language } 
        : { email, language: i18n.language };
      await axios.post(`${API}/auth/send-otp`, payload);
      toast.success(t('auth.code_sent'));
    } catch (error) {
      toast.error(t('auth.resend_failed'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep('form');
    setOtp('');
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1761798983107-a48299f8fb45?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="absolute inset-0 gradient-warm opacity-80" />

      {/* Language selector */}
      <div className="absolute top-6 right-6 z-10 w-36">
        <LanguageSelector className="bg-white/90 border-white/50 text-sm h-9" />
      </div>

      {/* Back button */}
      <button 
        onClick={() => step === 'otp' ? resetForm() : navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-2 text-[#718096] hover:text-[#2D3748] transition-colors z-10"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-5 h-5" />
        {step === 'otp' ? t('common.back') : t('common.back')}
      </button>

      <Card className="w-full max-w-md relative z-10 shadow-float border-0 rounded-3xl overflow-hidden">
        <CardHeader className="text-center pb-2 pt-8">
          <div className="flex justify-center mb-4">
            <div className="animate-pulse-slow">
              <CalvioLogo size={64} />
            </div>
          </div>
          <CardTitle className="text-2xl font-serif">
            {step === 'otp' ? t('auth.verify_code') : (mode === 'signin' ? t('auth.welcome_back') : t('auth.welcome_new'))}
          </CardTitle>
          <CardDescription className="text-[#718096]">
            {step === 'otp' 
              ? t('auth.check_email')
              : (mode === 'signin' 
                ? t('auth.sign_in_subtitle')
                : t('auth.sign_up_subtitle')
              )
            }
          </CardDescription>
        </CardHeader>

        <CardContent className="p-8">
          {step === 'form' ? (
            <>
              {/* Sign In / Sign Up Toggle */}
              <div className="flex bg-stone-100 rounded-xl p-1 mb-6">
                <button
                  onClick={() => setMode('signin')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    mode === 'signin' 
                      ? 'bg-white text-[#2D3748] shadow-sm' 
                      : 'text-[#718096] hover:text-[#2D3748]'
                  }`}
                >
                  <LogIn className="w-4 h-4" />
                  {t('common.login')}
                </button>
                <button
                  onClick={() => setMode('signup')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    mode === 'signup' 
                      ? 'bg-white text-[#2D3748] shadow-sm' 
                      : 'text-[#718096] hover:text-[#2D3748]'
                  }`}
                >
                  <UserPlus className="w-4 h-4" />
                  {t('common.signup')}
                </button>
              </div>

              <Tabs defaultValue="email" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-stone-100 rounded-xl p-1">
                  <TabsTrigger value="email" className="rounded-lg data-[state=active]:bg-white">{t('auth.email')}</TabsTrigger>
                  <TabsTrigger value="google" className="rounded-lg data-[state=active]:bg-white">{t('auth.google')}</TabsTrigger>
                </TabsList>
                
                <TabsContent value="email">
                  <form onSubmit={handleSendOTP} className="space-y-4">
                    {/* Name field - only for Sign Up */}
                    {mode === 'signup' && (
                      <div>
                        <label className="block text-sm font-medium text-[#2D3748] mb-1.5">
                          {t('auth.name')} <span className="text-red-400">*</span>
                        </label>
                        <Input
                          type="text"
                          placeholder={t('auth.name_placeholder')}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="input-base"
                          required={mode === 'signup'}
                          data-testid="name-input"
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-[#2D3748] mb-1.5">
                        {t('auth.email')} <span className="text-red-400">*</span>
                      </label>
                      <Input
                        type="email"
                        placeholder={t('auth.email_placeholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-base"
                        required
                        data-testid="email-input"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full btn-primary"
                      disabled={loading}
                      data-testid="send-otp-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Mail className="w-5 h-5 mr-2" />
                          {mode === 'signin' ? t('auth.send_code') : t('auth.send_signup_code')}
                        </>
                      )}
                    </Button>
                  </form>

                  {/* Switch mode link */}
                  <p className="text-center text-sm text-[#718096] mt-4">
                    {mode === 'signin' ? (
                      <>
                        {t('auth.no_account')}{' '}
                        <button 
                          onClick={() => setMode('signup')}
                          className="text-[#76A686] hover:text-[#5a8a6a] font-medium"
                        >
                          {t('common.signup')}
                        </button>
                      </>
                    ) : (
                      <>
                        {t('auth.have_account')}{' '}
                        <button 
                          onClick={() => setMode('signin')}
                          className="text-[#76A686] hover:text-[#5a8a6a] font-medium"
                        >
                          {t('common.login')}
                        </button>
                      </>
                    )}
                  </p>
                </TabsContent>
                
                <TabsContent value="google">
                  <div
                    className="w-full flex justify-center py-2"
                    data-testid="google-login-btn"
                  >
                    {googleLoading ? (
                      <div className="flex items-center gap-2 text-[#2D3748]">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>{t('auth.signing_in', 'Signing in...')}</span>
                      </div>
                    ) : (
                      <GoogleLogin
                        onSuccess={handleGoogleCredential}
                        onError={handleGoogleError}
                        useOneTap={false}
                        theme="outline"
                        size="large"
                        shape="rectangular"
                        text={mode === 'signup' ? 'signup_with' : 'signin_with'}
                        locale={i18n.language}
                        width="320"
                      />
                    )}
                  </div>
                  <p className="text-center text-xs text-[#A0AEC0] mt-4">
                    {t('auth.no_account')} / {t('auth.have_account')}
                  </p>
                </TabsContent>
              </Tabs>
            </>
          ) : (
            /* OTP Verification Step */
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#F0F7F4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-[#76A686]" />
                </div>
                <p className="text-[#718096] mb-1">{t('auth.check_email')}</p>
                <p className="font-medium text-[#2D3748]">{email}</p>
              </div>
              
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={(value) => {
                    setOtp(value);
                    if (value.length === 6) {
                      handleVerifyOTP(value);
                    }
                  }}
                  disabled={loading}
                  data-testid="otp-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot 
                        key={index} 
                        index={index}
                        className="w-12 h-14 text-xl border-stone-200 rounded-xl"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {loading && (
                <div className="flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-[#76A686]" />
                </div>
              )}
              
              <div className="text-center space-y-3">
                <button
                  onClick={handleResendCode}
                  disabled={loading}
                  className="text-sm text-[#76A686] hover:text-[#5a8a6a] transition-colors disabled:opacity-50"
                >
                  {t('auth.didnt_receive_code')}
                </button>
                
                <p className="text-xs text-[#A0AEC0]">
                  {t('auth.check_spam')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
